import { Fragment } from "react";

import cn from "classnames";

import { Text } from "@/components/text/text.component";

import { brown } from "@/app/font";
import type { AppRatingsComparisonTable as AppRatingsComparisonTableData } from "@/sanity.types";

import {
  RATING_VALUE_TEXT_CLASS,
  rowDividerClass,
} from "./appRatingsComparisonTable.tableUtils";
import type { ComparisonColumn } from "./appRatingsComparisonTable.types";
import { BrandHeaderGradientRule } from "./brandHeaderGradientRule.component";
import { ColumnHeader } from "./columnHeader.component";

export function MobileRatingsTable({
  columns,
  rows,
  firstColumnHeader,
  highlightIndex,
}: MobileRatingTableProps) {
  const gridTemplateColumns = `repeat(${1 + rows.length}, minmax(0, 1fr))`;

  return (
    <div
      role="table"
      aria-label="See how we compare to our competitors"
      className={cn(
        "lg:hidden",
        "max-lg:relative max-lg:ml-[calc(50%-_50vw)] max-lg:w-screen max-lg:max-w-[100vw]",
      )}>
      <div className="overflow-x-clip">
        <div
          role="row"
          className="grid w-full min-w-0"
          style={{ gridTemplateColumns: gridTemplateColumns }}>
          <div
            className="text-greyscale-body min-h-[3rem] pb-3 max-lg:pl-3"
            role="columnheader">
            {firstColumnHeader ? (
              <Text
                variant="body2"
                component="span"
                className="font-medium"
                role="cell">
                {firstColumnHeader}
              </Text>
            ) : (
              <span className="sr-only" role="cell">
                Phone Stores
              </span>
            )}
          </div>

          {rows.map((row) => (
            <div
              key={`m-h-${row._key}`}
              className="flex min-h-[3rem] items-end justify-center px-1 pb-3 text-center"
              role="columnheader">
              {/*
                Custom 16px bold (not `subtitle1`): that variant’s sm: breakpoint would
                override size on small screens.
              */}
              <Text
                role="cell"
                component="span"
                className={cn(
                  brown.className,
                  "text-greyscale-headline max-w-[5.5rem] text-center text-[16px] leading-[1.15] font-bold whitespace-normal",
                )}>
                {row.rowLabel}
              </Text>
            </div>
          ))}

          <BrandHeaderGradientRule />

          {columns.map((col, brandIndex) => {
            const isHighlighted = highlightIndex === brandIndex;
            const isFirstBrandRow = brandIndex === 0;

            return (
              <Fragment key={col._key}>
                <div
                  role="rowheader"
                  className={cn(
                    "text-greyscale-body border-greyscale-strength/20 box-border flex min-h-[53px] items-center justify-start py-0 pr-2 pl-0 max-lg:pl-4",
                    rowDividerClass(isFirstBrandRow),
                    isHighlighted &&
                      "bg-primary-100 rounded-l-xl max-lg:rounded-none",
                  )}>
                  <ColumnHeader
                    column={col as ComparisonColumn}
                    headerAlign="start"
                  />
                </div>
                {rows.map((row, rowIdx) => {
                  const isLastStoreColumn = rowIdx === rows.length - 1;
                  return (
                    <div
                      role="cell"
                      key={`${col._key}-${row._key}`}
                      className={cn(
                        "text-greyscale-body border-greyscale-strength/20 box-border flex min-h-[53px] items-center justify-center py-0 text-center",
                        rowDividerClass(isFirstBrandRow),
                        isHighlighted && "bg-primary-100",
                        isHighlighted &&
                          isLastStoreColumn &&
                          "rounded-r-xl max-lg:rounded-none",
                      )}>
                      <Text
                        variant="body1"
                        component="span"
                        className={RATING_VALUE_TEXT_CLASS}>
                        {row.values?.[brandIndex] ?? "—"}
                      </Text>
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type MobileRatingTableProps = {
  columns: NonNullable<AppRatingsComparisonTableData["columns"]>;
  rows: NonNullable<AppRatingsComparisonTableData["rows"]>;
  firstColumnHeader: string | null | undefined;
  highlightIndex: number;
};
